import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { 
  Search,
  Filter,
  Calendar,
  Building2, 
  CalendarDays, 
  Package, 
  ChevronRight, 
  ArrowLeft,
  ShoppingBag,
  MapPin,
  Trash2,
  Edit3,
  CheckSquare,
  Square,
  Clock,
  Phone,
  Mail,
  UserCircle2,
  ChevronUp
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { QRCodeCanvas } from 'qrcode.react';
import CuteEmoji from '@/components/shared/CuteEmoji';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import ReadOnlyInvoiceModal from '@/components/client-po/ReadOnlyInvoiceModal';

export default function ClientData() {
  const { member } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const isPurchasing = location.pathname.includes('/purchasing');

  const [selectedClient, setSelectedClient] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('po'); // 'po' | 'inventori' | 'detail_pcs'
  const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'custom'
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [selectedPOForModal, setSelectedPOForModal] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // List View Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('terbaru'); // 'terbaru', 'terlama', 'nama_az', 'nama_za', 'po_banyak', 'po_sedikit'
  const [poTimeFilter, setPoTimeFilter] = useState('all'); // '1_hari', '7_hari', '1_bulan', 'all', 'custom'
  const [poCustomStart, setPoCustomStart] = useState('');
  const [poCustomEnd, setPoCustomEnd] = useState('');

  const { data: teamMembers = [] } = useQuery({ queryKey: ['team-members'], queryFn: () => base44.entities.TeamMember.list() });
  const queryClient = useQueryClient();
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editForm, setEditForm] = useState({});
  const updateClient = useMutation({
    mutationFn: async (data) => {
      if (selectedClient.isUnverified) {
         return await base44.entities.ClientAccount.create({
           brand_name: selectedClient.brand_name,
           company_name: selectedClient.company_name,
           client_type: data.client_type,
           npwp: data.npwp,
           status: 'verified',
           owner_name: data.owner_name,
           pic_name: data.pic_name,
           phone: data.phone,
           location: data.location,
           addresses: data.addresses,
           email: data.email
         });
      } else {
         return await base44.entities.ClientAccount.update(selectedClient.id, data);
      }
    },
    onSuccess: (savedData) => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      setIsEditingClient(false);
      setSelectedClient(prev => ({...prev, ...editForm, isUnverified: false, id: prev.isUnverified ? savedData.id : prev.id}));
    }
  });

  // Real data
  const { data: clientAccounts = [], isLoading } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });
  const { clientPOs } = useClientPOs();

  // Inventori Data
  const { data: inventoryPackaging = [] } = useQuery({ 
    queryKey: ['packaging-client-data'], 
    queryFn: async () => {
      const [b, t, s] = await Promise.all([base44.entities.Botol.list(), base44.entities.Tutup.list(), base44.entities.Spray.list()]);
      return [...b.map(i => ({...i, _tipe: 'botol'})), ...t.map(i => ({...i, _tipe: 'tutup'})), ...s.map(i => ({...i, _tipe: 'spray'}))];
    }
  });
  const { data: inventoryItemTambahan = [] } = useQuery({ queryKey: ['item-tambahan-client-data'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: inventoryBahanCair = [] } = useQuery({ queryKey: ['bahan-cair-client-data'], queryFn: () => base44.entities.BahanCair.list() });

  // Enrich client accounts with PO data — Terintegrasi dengan Database Klien (Manajemen Tim)
  // Hanya tampilkan klien yang SUDAH terverifikasi, ATAU yang belum terverifikasi tapi PUNYA PO aktif.
  // Klien yang belum terverifikasi tanpa PO hanya muncul di Workspace > Klien Terverifikasi.
  const clientMap = new Map();
  const deletedBrands = new Set();

  clientAccounts.forEach(ca => {
    if (ca.brand_name) {
      const lowerBrand = ca.brand_name.toLowerCase();
      if (ca.status === 'deleted' || ca.is_deleted) {
        deletedBrands.add(lowerBrand);
        return;
      }
      
      // Sembunyikan akun developer/test
      if (ca.email === 'satalawebdeveloper@gmail.com') return;
      if (lowerBrand === 'developer' || lowerBrand === 'satala dev client' || lowerBrand === 'test') return;

      const isVerified = ca.status === 'verified';
      
      // Masukkan semua klien yang ada di ClientAccount (termasuk unverified tanpa PO, spt dari Sample)
      clientMap.set(ca.brand_name.toLowerCase(), { ...ca, isUnverified: !isVerified });
    }
  });

  // Auto-generate entry dari PO yang brand-nya belum ada di ClientAccount sama sekali
  clientPOs.forEach(po => {
    const poBrand = po.customerName ? po.customerName.split('/')[0].trim() : '';
    if (poBrand) {
      const lowerBrand = poBrand.toLowerCase();
      if (deletedBrands.has(lowerBrand)) return;
      
      // Sembunyikan data developer dari PO
      if (lowerBrand === 'developer' || lowerBrand === 'satala dev client' || lowerBrand === 'test') return;

      if (!clientMap.has(lowerBrand)) {
        clientMap.set(lowerBrand, {
          id: `unverified-${poBrand}`,
          brand_name: poBrand,
          company_name: po.customerName || poBrand,
          status: 'unverified',
          isUnverified: true,
          created_date: po.created_date,
          location: '-',
          owner_name: '-'
        });
      }
    }
  });

  const enrichedClients = Array.from(clientMap.values()).map(ca => {
    const brandPOs = clientPOs.filter(po => {
      const poBrand = po.customerName ? po.customerName.split('/')[0].trim() : '';
      return poBrand.toLowerCase() === ca.brand_name.toLowerCase();
    });
    const totalPcs = brandPOs.reduce((sum, po) => {
      return sum + (po.products || []).reduce((s, p) => s + (Number(p.qty) || 0), 0);
    }, 0);

    // Calculate mood
    const now = new Date();
    const activePOs = brandPOs.filter(po => {
      const prodJual = (po.products || []).reduce((s, p) => s + (Number(p.total_harga_jual) || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (Number(l.harga_jual) * (Number(l.qty_variant) || 1)), 0);
      const nominalPPN = po.ppn_mode === 'exclude' ? ((prodJual + legJual) * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalJual = prodJual + legJual;
      const totalTagihan = totalJual + nominalPPN;
      const totalBayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const isLunas = totalBayar >= totalTagihan && totalTagihan > 0;
      if (po.statusProduksi === 'SUDAH SAMPAI' && isLunas) return false;
      return !po.timeline?.some(t => t.status === 'Pesanan Selesai' && t.done);
    });

    let lastPoDate = null;
    let autoPic = null;
    brandPOs.forEach(po => {
      const rawDateStr = po.created_at || po.date || po.tanggal || po.created_date;
      let d = null;
      if (rawDateStr) {
        let parsed = new Date(rawDateStr);
        if (isNaN(parsed.getTime()) && typeof rawDateStr === 'string') {
          // Handle '1 Juli 2026' format manually if V8 fails
          const parts = rawDateStr.split(' ');
          if (parts.length >= 3) {
            const months = { 'januari':'01', 'februari':'02', 'maret':'03', 'april':'04', 'mei':'05', 'juni':'06', 'juli':'07', 'agustus':'08', 'september':'09', 'oktober':'10', 'november':'11', 'desember':'12' };
            const m = months[parts[1].toLowerCase()] || '01';
            parsed = new Date(`${parts[2]}-${m}-${parts[0].padStart(2, '0')}`);
          }
        }
        if (!isNaN(parsed.getTime())) d = parsed;
      }
      if (d && (!lastPoDate || d > lastPoDate)) {
        lastPoDate = d;
        if (po.pic) autoPic = po.pic;
      }
    });

    let daysSinceLastPO = lastPoDate ? Math.floor((now - lastPoDate) / (1000 * 60 * 60 * 24)) : 9999;
    
    let mood = ca.manual_mood || null;
    if (!mood) {
      if (activePOs.length === 0 && daysSinceLastPO > 210) mood = 'inactive';
      else if (activePOs.length === 0 && daysSinceLastPO > 60) mood = 'pasif';
      else mood = 'happy'; // simplified for card view
    }

    return {
      ...ca,
      pic_name: ca.pic_name || ca.pic || autoPic || '',
      totalOrders: brandPOs.length,
      totalPcs,
      activePOsCount: activePOs.length,
      mood,
      lastPoDate,
      allPOs: brandPOs,
      poHistory: brandPOs.slice(0, 10).map(po => {
        let parsedDate = po.created_date ? new Date(po.created_date) : null;
        if (parsedDate && isNaN(parsedDate.getTime())) {
           // Fallback for DD/MM/YYYY or DD-MM-YYYY
           const parts = po.created_date.split(/[-/]/);
           if (parts.length === 3) parsedDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
        return {
          id: po.id,
          date: parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : (po.created_date || '-'),
          totalPcs: (po.products || []).reduce((s, p) => s + (p.qty || 0), 0),
          variants: (po.products || []).map(p => p.nama_product || 'Varian'),
          status: po.statusProduksi || po.statusBayar || 'N/A',
        };
      }),
      titipan: {
        packaging: inventoryPackaging.filter(i => i.dari_client && i.client_owner?.toLowerCase() === ca.brand_name.toLowerCase()),
        itemTambahan: inventoryItemTambahan.filter(i => i.dari_client && i.client_owner?.toLowerCase() === ca.brand_name.toLowerCase()),
        bahanCair: inventoryBahanCair.filter(i => i.dari_client && i.client_owner?.toLowerCase() === ca.brand_name.toLowerCase()),
      }
    };
  }).filter(ca => !ca.isUnverified || (ca.allPOs && ca.allPOs.length > 0) || ca.totalOrders > 0).sort((a, b) => (a.brand_name || '').localeCompare(b.brand_name || '', 'id'));

  const preFilteredClients = enrichedClients.filter(ca => {
    if (isPurchasing) return true; // Purchasing sees all
    
    // Marketing sees only their own, unless they are admin/manager/developer
    const role = (member?.role || '').toLowerCase();
    const position = (member?.position || member?.jabatan || '').toLowerCase();
    const isOwner = ['developer', 'owner', 'admin', 'manager', 'finance', 'direktur'].some(r => role.includes(r) || position.includes(r));
    
    if (isOwner) return true;
    
    // Explicit Access Check (jika diberi akses ke data_klien, maka bisa lihat semua data)
    const hasDataKlienAccess = member?.page_access?.data_klien === 'edit' || member?.page_access?.data_klien === 'view';
    if (hasDataKlienAccess) return true;
    
    const memberName = (member?.full_name || member?.nama || member?.name || '').toLowerCase();
    const picLower = (ca.pic_name || ca.pic || '').toLowerCase();
    if (picLower && (picLower === memberName || memberName.includes(picLower) || picLower.includes(memberName))) return true;
    // Show if unassigned so it's not lost
    return !ca.pic_name || ca.pic_name === '-' || ca.pic_name === '';
  });

  // Apply Search, Sort, and Time Filter
  const filteredEnrichedClients = preFilteredClients.filter(ca => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (ca.brand_name || '').toLowerCase().includes(q) || 
           (ca.owner_name || '').toLowerCase().includes(q) ||
           (ca.pic_name || '').toLowerCase().includes(q);
  }).map(ca => {
    // If we sort by PO, we need to know the PO count in the specific timeframe
    if (sortOption === 'po_banyak' || sortOption === 'po_sedikit') {
       const now = new Date();
       const filteredPOs = ca.allPOs.filter(po => {
          if (poTimeFilter === 'all') return true;
          const poDate = new Date(po.created_date);
          if (isNaN(poDate)) return false;
          
          poDate.setHours(0,0,0,0);
          const today = new Date();
          today.setHours(0,0,0,0);

          if (poTimeFilter === '1_hari') {
             return poDate.getTime() === today.getTime();
          } else if (poTimeFilter === '7_hari') {
             const limit = new Date(today);
             limit.setDate(limit.getDate() - 7);
             return poDate >= limit;
          } else if (poTimeFilter === '1_bulan') {
             const limit = new Date(today);
             limit.setMonth(limit.getMonth() - 1);
             return poDate >= limit;
          } else if (poTimeFilter === 'custom') {
             if (poCustomStart) {
                const s = new Date(poCustomStart);
                s.setHours(0,0,0,0);
                if (poDate < s) return false;
             }
             if (poCustomEnd) {
                const e = new Date(poCustomEnd);
                e.setHours(23,59,59,999);
                if (poDate > e) return false;
             }
             return true;
          }
          return true;
       });
       return { ...ca, sortPoCount: filteredPOs.length };
    }
    return { ...ca, sortPoCount: ca.totalOrders };
  }).sort((a, b) => {
    const parseFallback = (client) => {
      const raw = client.joined_date || client.created_at || client.created_date;
      if (!raw) return 0;
      let parsed = new Date(raw);
      if (isNaN(parsed.getTime()) && typeof raw === 'string') {
        const parts = raw.split(' ');
        if (parts.length >= 3) {
          const months = { 'januari':'01', 'februari':'02', 'maret':'03', 'april':'04', 'mei':'05', 'juni':'06', 'juli':'07', 'agustus':'08', 'september':'09', 'oktober':'10', 'november':'11', 'desember':'12' };
          const m = months[parts[1].toLowerCase()] || '01';
          parsed = new Date(`${parts[2]}-${m}-${parts[0].padStart(2, '0')}`);
        }
      }
      return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };
    
    if (sortOption === 'nama_az') return (a.brand_name || '').localeCompare(b.brand_name || '', 'id');
    if (sortOption === 'nama_za') return (b.brand_name || '').localeCompare(a.brand_name || '', 'id');
    if (sortOption === 'terbaru') return (b.lastPoDate?.getTime() || parseFallback(b)) - (a.lastPoDate?.getTime() || parseFallback(a));
    if (sortOption === 'terlama') return (a.lastPoDate?.getTime() || parseFallback(a)) - (b.lastPoDate?.getTime() || parseFallback(b));
    if (sortOption === 'po_banyak') return b.sortPoCount - a.sortPoCount;
    if (sortOption === 'po_sedikit') return a.sortPoCount - b.sortPoCount;
    return 0;
  });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount
  } = useBatchSelection(filteredEnrichedClients, c => c.id);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      let success = 0;
      for (const id of ids) {
        if (id.toString().startsWith('unverified-')) {
          // Buat record dummy di ClientAccount dengan status 'deleted' agar selamanya di-hide
          const brandName = id.replace('unverified-', '');
          await base44.entities.ClientAccount.create({
            brand_name: brandName,
            status: 'deleted'
          });
          success++;
        } else {
          // Soft delete klien yang sudah terverifikasi
          await base44.entities.ClientAccount.update(id, { status: 'deleted' });
          success++;
        }
      }
      return success;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      clearSelection();
      alert(`${count} brand berhasil dihapus/disembunyikan.`);
    }
  });

  // ── Detail View ──
  if (selectedClient) {
    // Apply date filter
    const filteredPOs = selectedClient.allPOs.filter(po => {
      if (dateFilterType === 'all') return true;
      const dateStr = po.date || po.created_date;
      if (!dateStr) return false;
      
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;

      // set hours to 0 for accurate date comparison
      d.setHours(0,0,0,0);
      
      if (dateStart) {
        const ds = new Date(dateStart);
        ds.setHours(0,0,0,0);
        if (d < ds) return false;
      }
      if (dateEnd) {
        const de = new Date(dateEnd);
        de.setHours(23,59,59,999);
        if (d > de) return false;
      }
      return true;
    });

    const filteredTotalOrders = filteredPOs.length;
    const filteredTotalPcs = filteredPOs.reduce((sum, po) => sum + (po.products || []).reduce((s, p) => s + (Number(p.qty) || 0), 0), 0);
    const filteredTotalHargaJual = filteredPOs.reduce((sum, po) => {
      const prodJual = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      return sum + prodJual + legJual;
    }, 0);

    const formatRupiah = (angka) => {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(angka);
    };

    return (
      <div className="space-y-6 pb-16 text-[#302621] animate-in fade-in slide-in-from-right-4 duration-500">
        <button 
          onClick={() => {
            setSelectedClient(null);
            setActiveDetailTab('po');
            setDateFilterType('all');
            setDateStart('');
            setDateEnd('');
            setIsEditingClient(false);
          }}
          className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Klien
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info Card */}
          <Card className="lg:col-span-1 border-none shadow-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden rounded-[2rem]">
            <CardContent className="p-8 relative">
              <div className="absolute top-0 right-0 p-6 opacity-10">
                <Building2 className="w-32 h-32" />
              </div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                   <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center  border border-white/20">
                     <Building2 className="w-8 h-8 text-white" />
                   </div>
                   <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center  border border-white/20 shadow-lg overflow-hidden" title={`Mood: ${selectedClient.mood}`}>
                     <CuteEmoji mood={selectedClient.mood} className="w-14 h-14" />
                   </div>
                </div>
                <div className="mb-4">
                  {!isEditingClient ? (
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h2 className="text-2xl md:text-3xl font-black tracking-tight">{selectedClient.brand_name}</h2>
                        {selectedClient.company_name && (
                          <p className="text-xs text-slate-300 font-medium mt-0.5">{selectedClient.company_name}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 shrink-0" onClick={() => {
                        setEditForm({
                          brand_name: selectedClient.brand_name || '',
                          company_name: selectedClient.company_name || '',
                          owner_name: selectedClient.owner_name !== '-' ? selectedClient.owner_name : '',
                          pic_name: selectedClient.pic_name || selectedClient.pic || '',
                          phone: selectedClient.phone !== '-' ? selectedClient.phone : '',
                          location: selectedClient.location !== '-' ? selectedClient.location : '',
                          addresses: selectedClient.addresses || (selectedClient.location && selectedClient.location !== '-' ? [{ id: 'loc-1', penerima: selectedClient.pic_name || selectedClient.pic || selectedClient.owner_name, telepon: selectedClient.phone !== '-' ? selectedClient.phone : '', alamat_lengkap: selectedClient.location, kode_pos: '' }] : []),
                          email: selectedClient.email || '',
                          client_type: selectedClient.client_type || 'Perusahaan',
                          npwp: selectedClient.npwp !== '-' ? selectedClient.npwp : ''
                        });
                        setIsEditingClient(true);
                      }}>Edit Data</Button>
                    </div>
                  ) : (
                    <div className="space-y-3 bg-white/10 p-3 rounded-xl border border-white/20 mb-3">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2">
                        <span className="text-xs font-bold text-white uppercase tracking-wider">Edit Profile Klien</span>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 h-7 text-xs px-2" onClick={() => setIsEditingClient(false)}>Batal</Button>
                          <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-xs px-3" onClick={() => updateClient.mutate(editForm)} disabled={updateClient.isPending}>
                            {updateClient.isPending ? 'Menyimpan...' : 'Simpan'}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] text-slate-300 font-bold uppercase tracking-wider block mb-1">Nama Brand / Klien</label>
                        <input 
                          type="text" 
                          value={editForm.brand_name || ''} 
                          onChange={e => setEditForm({...editForm, brand_name: e.target.value})}
                          className="w-full bg-slate-900/80 border border-white/30 rounded-md px-3 py-1.5 text-sm text-white font-bold focus:outline-none focus:border-white/60"
                          placeholder="Nama Brand"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] text-slate-300 font-bold uppercase tracking-wider block mb-1">Nama Perusahaan (Opsional)</label>
                        <input 
                          type="text" 
                          value={editForm.company_name || ''} 
                          onChange={e => setEditForm({...editForm, company_name: e.target.value})}
                          className="w-full bg-slate-900/80 border border-white/30 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-white/60"
                          placeholder="PT / CV Nama Perusahaan"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-slate-300 font-medium mb-8">ID: {selectedClient.id}</p>

                {selectedClient.status === 'verified' && (
                  <div className="mb-6 flex flex-col items-center bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs font-bold text-white/50 mb-3 uppercase tracking-wider">QR Code Klien</p>
                    <div className="bg-white p-2 rounded-xl mb-3">
                      <QRCodeCanvas 
                        id={`qr-${selectedClient.id}`}
                        value={selectedClient.id} 
                        size={120} 
                        level="H" 
                        includeMargin={false}
                      />
                    </div>
                    <Button 
                      onClick={() => {
                        const canvas = document.getElementById(`qr-${selectedClient.id}`);
                        if (canvas) {
                          const url = canvas.toDataURL('image/png');
                          const link = document.createElement('a');
                          link.download = `QR-Klien-${selectedClient.brand_name}.png`;
                          link.href = url;
                          link.click();
                        }
                      }}
                      variant="outline" 
                      className="w-full text-xs h-8 border-white/20 hover:bg-white/10 text-white bg-transparent"
                    >
                      Unduh QR Code
                    </Button>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-1">
                      <UserCircle2 className="w-4 h-4" />
                    </div>
                    <div className="w-full">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Tipe Klien</p>
                      {isEditingClient ? (
                        <Select 
                          value={editForm.client_type || 'Perusahaan'} 
                          onValueChange={val => setEditForm({...editForm, client_type: val})}
                        >
                          <SelectTrigger className="w-full bg-slate-800 border-white/20 text-white h-[34px] rounded-md focus:ring-0 focus:ring-offset-0 focus:border-white/40 text-sm mb-3">
                            <SelectValue placeholder="Pilih Tipe" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="Perusahaan" className="text-slate-800">Perusahaan</SelectItem>
                            <SelectItem value="Pribadi" className="text-slate-800">Pribadi</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm font-bold mb-3">{selectedClient.client_type || 'Perusahaan'}</p>
                      )}

                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">NPWP / NIK</p>
                      {isEditingClient ? (
                        <input 
                          type="text" 
                          value={editForm.npwp || ''} 
                          onChange={e => setEditForm({...editForm, npwp: e.target.value})}
                          className="w-full bg-black/20 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white mb-3 focus:outline-none focus:border-white/40 transition-colors"
                          placeholder="Nomor NPWP atau NIK"
                        />
                      ) : (
                        <p className="text-sm font-bold mb-3">{selectedClient.npwp || '-'}</p>
                      )}

                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Owner</p>
                      {isEditingClient ? (
                        <input 
                          type="text" 
                          value={editForm.owner_name} 
                          onChange={e => setEditForm({...editForm, owner_name: e.target.value})}
                          className="w-full bg-black/20 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white mb-3 focus:outline-none focus:border-white/40 transition-colors"
                          placeholder="Nama Owner"
                        />
                      ) : (
                        <p className="text-sm font-bold mb-3">{selectedClient.owner_name || '-'}</p>
                      )}

                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">PIC</p>
                      {isEditingClient ? (
                        <Select 
                          value={editForm.pic_name || ''} 
                          onValueChange={val => setEditForm({...editForm, pic_name: val})}
                        >
                          <SelectTrigger className="w-full bg-slate-800 border-white/20 text-white h-[34px] rounded-md focus:ring-0 focus:ring-offset-0 focus:border-white/40 text-sm">
                            <SelectValue placeholder="Pilih PIC" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            {teamMembers.map(tm => (
                              <SelectItem key={tm.id} value={tm.full_name} className="text-slate-800">
                                {tm.full_name} - <span className="text-[10px] text-slate-500">{tm.role}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm font-bold">{selectedClient.pic_name || selectedClient.pic || '-'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="w-full">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Telepon</p>
                      {isEditingClient ? (
                        <input 
                          type="text" 
                          value={editForm.phone} 
                          onChange={e => setEditForm({...editForm, phone: e.target.value})}
                          className="w-full bg-black/20 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/40 transition-colors"
                          placeholder="Nomor Telepon"
                        />
                      ) : (
                        <p className="text-sm font-bold">{selectedClient.phone || '-'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-1">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div className="w-full">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Daftar Alamat Pengiriman</p>
                      {isEditingClient ? (
                        <div className="space-y-3 mt-2">
                          {editForm.addresses?.map((addr, idx) => (
                            <div key={idx} className="bg-black/20 border border-white/20 rounded-md p-3 relative">
                              <button type="button" onClick={() => {
                                 const newArr = [...editForm.addresses];
                                 newArr.splice(idx, 1);
                                 setEditForm({...editForm, addresses: newArr});
                              }} className="absolute top-2 right-2 text-rose-400 hover:text-rose-300 text-xs font-bold">Hapus</button>
                              
                              <input type="text" value={addr.penerima} onChange={e => {
                                 const newArr = [...editForm.addresses]; newArr[idx].penerima = e.target.value; setEditForm({...editForm, addresses: newArr});
                              }} placeholder="Nama Penerima" className="w-full bg-transparent border-b border-white/10 text-sm text-white mb-2 pb-1 focus:outline-none focus:border-white/40" />
                              
                              <input type="text" value={addr.telepon} onChange={e => {
                                 const newArr = [...editForm.addresses]; newArr[idx].telepon = e.target.value; setEditForm({...editForm, addresses: newArr});
                              }} placeholder="No. Telepon" className="w-full bg-transparent border-b border-white/10 text-sm text-white mb-2 pb-1 focus:outline-none focus:border-white/40" />

                              <textarea value={addr.alamat_lengkap} onChange={e => {
                                 const newArr = [...editForm.addresses]; newArr[idx].alamat_lengkap = e.target.value; setEditForm({...editForm, addresses: newArr});
                              }} placeholder="Alamat Lengkap" className="w-full bg-transparent border-b border-white/10 text-sm text-white pb-1 focus:outline-none focus:border-white/40 resize-none h-12" />
                              
                              <input type="text" value={addr.kode_pos || ''} onChange={e => {
                                 const newArr = [...editForm.addresses]; newArr[idx].kode_pos = e.target.value; setEditForm({...editForm, addresses: newArr});
                              }} placeholder="Kode Pos (Opsional)" className="w-full bg-transparent border-b border-white/10 text-sm text-white mt-2 pb-1 focus:outline-none focus:border-white/40" />
                            </div>
                          ))}
                          <Button size="sm" variant="outline" className="w-full text-xs border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => {
                             setEditForm({...editForm, addresses: [...(editForm.addresses || []), { id: `addr-${Date.now()}`, penerima: '', telepon: '', alamat_lengkap: '', kode_pos: '' }]});
                          }}>+ Tambah Alamat</Button>
                        </div>
                      ) : (
                        <div className="space-y-2 mt-1">
                          {selectedClient.addresses && selectedClient.addresses.length > 0 ? (
                            selectedClient.addresses.map((addr, idx) => (
                              <div key={idx} className="bg-white/5 border border-white/10 rounded-md p-3">
                                <p className="text-sm font-bold text-white mb-1">{addr.penerima} <span className="text-slate-400 text-xs font-normal">({addr.telepon})</span></p>
                                <p className="text-xs text-slate-300 leading-relaxed">{addr.alamat_lengkap}</p>
                                {addr.kode_pos && <p className="text-[10px] text-slate-400 mt-1 font-mono">Kode Pos: {addr.kode_pos}</p>}
                              </div>
                            ))
                          ) : (
                            <p className="text-sm font-bold">{selectedClient.location || '-'}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="w-full">
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Email</p>
                      {isEditingClient ? (
                        <input 
                          type="email" 
                          value={editForm.email} 
                          onChange={e => setEditForm({...editForm, email: e.target.value})}
                          className="w-full bg-black/20 border border-white/20 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/40 transition-colors"
                          placeholder="Alamat Email"
                        />
                      ) : (
                        <p className="text-sm font-bold">{selectedClient.email || '-'}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Bergabung Sejak</p>
                      <p className="text-sm font-bold">{selectedClient.created_date ? new Date(selectedClient.created_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                   <div className="grid grid-cols-2 gap-4 text-center mb-4">
                      <div className="bg-white/5 rounded-xl p-3">
                         <p className="text-xl md:text-2xl font-black">{filteredTotalOrders}</p>
                         <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total PO</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-3">
                         <p className="text-xl md:text-2xl font-black">{filteredTotalPcs.toLocaleString()}</p>
                         <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total Pcs</p>
                      </div>
                   </div>
                   <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white/5 rounded-xl p-3">
                         <p className="text-lg md:text-xl font-black">{filteredTotalOrders}</p>
                         <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total Invoice</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-3">
                         <p className="text-xs md:text-sm font-black pt-1">{formatRupiah(filteredTotalHargaJual)}</p>
                         <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">Total Harga Jual</p>
                      </div>
                   </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detail Tabs */}
          <Card className="lg:col-span-2 border border-slate-200/50 shadow-sm bg-white rounded-[2rem]">
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setActiveDetailTab('po')}
                className={`flex-1 py-4 text-sm font-bold text-center border-b-2 transition-colors ${
                  activeDetailTab === 'po' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Riwayat PO
              </button>
              <button
                onClick={() => setActiveDetailTab('inventori')}
                className={`flex-1 py-4 text-sm font-bold text-center border-b-2 transition-colors ${
                  activeDetailTab === 'inventori' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Barang Titipan (Inventori)
              </button>
              <button
                onClick={() => setActiveDetailTab('detail_pcs')}
                className={`flex-1 py-4 text-sm font-bold text-center border-b-2 transition-colors ${
                  activeDetailTab === 'detail_pcs' ? 'border-[#8C6208] text-[#8C6208]' : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Detail Pcs
              </button>
            </div>

            <CardContent className="p-8">
              {(activeDetailTab === 'po' || activeDetailTab === 'detail_pcs') && (
                <div className="mb-8 p-4 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Periode Tanggal</label>
                    <div className="flex gap-2 bg-white p-1 rounded-lg border border-slate-200 w-fit">
                      <button 
                        onClick={() => setDateFilterType('all')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${dateFilterType === 'all' ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                      >
                        All Time
                      </button>
                      <button 
                        onClick={() => setDateFilterType('custom')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${dateFilterType === 'custom' ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                      >
                        Tanggal Tertentu
                      </button>
                    </div>
                  </div>
                  {dateFilterType === 'custom' && (
                    <>
                      <div className="flex-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Dari Tanggal</label>
                        <input 
                          type="date" 
                          value={dateStart} 
                          onChange={setDateStart}
                          className="w-full text-sm rounded-lg border-slate-200 bg-white"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Sampai Tanggal</label>
                        <input 
                          type="date" 
                          value={dateEnd} 
                          onChange={setDateEnd}
                          className="w-full text-sm rounded-lg border-slate-200 bg-white"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeDetailTab === 'po' && (
                <>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight mb-8 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" /> Riwayat Purchase Order
                  </h3>

                  {filteredPOs.length === 0 ? (
                    <div className="text-center py-16 text-slate-400">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="font-medium">Belum ada riwayat PO</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {filteredPOs.map((po, index) => {
                        const dateStr = po.date || po.created_date;
                        const poDate = dateStr ? new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
                        const poStatus = po.statusProduksi || po.statusBayar || 'N/A';
                        const poTotalPcs = (po.products || []).reduce((s, p) => s + (Number(p.qty) || 0), 0);
                        const prodJual = (po.products || []).reduce((s, p) => s + (Number(p.total_harga_jual) || 0), 0);
                        const legJual = (po.legalitas || []).reduce((s, l) => s + ((Number(l.harga_jual) || 0) * (Number(l.qty_variant) || 1)), 0);
                        const poTotalJual = prodJual + legJual; // Omset Murni
                        
                        const poVariants = [...new Set((po.products || []).map(p => p.nama_product || 'Varian'))];

                        return (
                        <div key={po.id} className="relative pl-8">
                          {index !== filteredPOs.length - 1 && (
                            <div className="absolute left-[11px] top-8 bottom-[-24px] w-[2px] bg-slate-100" />
                          )}
                          <div className="absolute left-0 top-1.5 w-6 h-6 rounded-full border-4 border-white bg-amber-500 shadow-sm" />

                          <div 
                            className="bg-slate-50 border border-slate-100 rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer hover:border-[#8C6208]/30 group"
                            onClick={(e) => {
                              navigate('./invoice/' + po.id, { state: { po } });
                            }}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-mono font-bold text-slate-500">#{po.id}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${
                                     poStatus.includes('SELESAI') || poStatus === 'LUNAS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                     poStatus.includes('PROSES') || poStatus.includes('PRODUKSI') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                     'bg-blue-50 text-blue-700 border-blue-200'
                                  }`}>
                                    {poStatus}
                                  </span>
                                </div>
                                <p className="text-sm font-bold text-slate-800 group-hover:text-[#8C6208] transition-colors">{poDate}</p>
                              </div>
                              <div className="flex gap-6 text-left sm:text-right mt-3 sm:mt-0">
                                <div className="text-right">
                                  <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Total Harga Jual</p>
                                  <p className="text-lg font-black text-emerald-600">{formatRupiah(poTotalJual)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Total Quantity</p>
                                  <p className="text-lg font-black text-[#8C6208]">{poTotalPcs.toLocaleString()} Pcs</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-white rounded-xl p-3 border border-slate-100 flex justify-between items-center">
                               <div>
                                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                   <Package className="w-3 h-3" /> Varian Produk
                                 </p>
                                 <div className="flex flex-wrap gap-2">
                                   {poVariants.map((v, i) => (
                                     <span key={i} className="px-2.5 py-1 bg-slate-50 text-slate-700 text-xs font-semibold rounded-md border border-slate-200">
                                       {v}
                                     </span>
                                   ))}
                                 </div>
                               </div>
                               <Button variant="ghost" size="sm" className="hidden sm:flex text-xs font-bold text-[#8C6208] opacity-0 group-hover:opacity-100 transition-opacity">
                                 Lihat Detail <ChevronRight className="w-4 h-4 ml-1" />
                               </Button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {activeDetailTab === 'detail_pcs' && (
                <>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight mb-8 flex items-center gap-2">
                    <Package className="w-5 h-5 text-indigo-500" /> Detail Pcs per Varian
                  </h3>
                  
                  {filteredPOs.length === 0 ? (
                    <div className="text-center py-16 text-slate-400">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="font-medium">Belum ada data untuk periode ini</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {filteredPOs.map((po) => {
                        if (!po.products || po.products.length === 0) return null;
                        
                        // Group by variant and size
                        const variantMap = {};
                        po.products.forEach(p => {
                          const getUk = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || p.ukuran || 'Ukuran';
                          const ukStr = (getUk !== 'Ukuran' && !String(getUk).toLowerCase().includes('ml')) ? `${getUk}ml` : getUk;
                          const key = `${p.nama_product || 'Varian'} - ${ukStr}`;
                          if (!variantMap[key]) {
                            variantMap[key] = { nama: p.nama_product || 'Varian', ukuran: ukStr, qty: 0 };
                          }
                          variantMap[key].qty += (p.qty || 0);
                        });
                        const variants = Object.values(variantMap);
                        
                        return (
                          <div key={po.id} className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-200">
                              <span className="text-sm font-bold text-slate-800">Invoice #{po.id}</span>
                              <span className="text-xs font-semibold text-slate-500">{(po.date || po.created_date) ? new Date(po.date || po.created_date).toLocaleDateString('id-ID') : '-'}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {variants.map((v, i) => (
                                <div key={i} className="bg-white border border-slate-100 rounded-lg p-3">
                                  <p className="text-xs font-bold text-slate-600 mb-1">{v.nama}</p>
                                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-50">
                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded">{v.ukuran}</span>
                                    <span className="text-sm font-black text-[#8C6208]">{v.qty.toLocaleString()} Pcs</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {activeDetailTab === 'inventori' && (
                <>
                  <h3 className="text-xl font-black text-slate-800 tracking-tight mb-8 flex items-center gap-2">
                    <Package className="w-5 h-5 text-sky-500" /> Barang Titipan Client
                  </h3>
                  
                  {(!selectedClient.titipan.packaging.length && !selectedClient.titipan.itemTambahan.length && !selectedClient.titipan.bahanCair.length) ? (
                    <div className="text-center py-16 text-slate-400">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="font-medium">Tidak ada barang titipan (Inventori)</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {selectedClient.titipan.packaging.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-600 mb-3 uppercase tracking-wider border-b border-slate-100 pb-2">Packaging</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {selectedClient.titipan.packaging.map(item => (
                              <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-bold text-sm text-[#302621]">{item.nama}</h5>
                                  <span className="bg-sky-100 text-sky-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">{item._tipe}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">Stok Tersedia:</p>
                                <p className="text-lg font-black text-[#8C6208]">{item.stok?.toLocaleString() || 0} pcs</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedClient.titipan.itemTambahan.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-600 mb-3 uppercase tracking-wider border-b border-slate-100 pb-2">Item Tambahan</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {selectedClient.titipan.itemTambahan.map(item => (
                              <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-bold text-sm text-[#302621]">{item.nama}</h5>
                                  <span className="bg-sky-100 text-sky-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">{item.kategori}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">Stok Tersedia:</p>
                                <p className="text-lg font-black text-[#8C6208]">{item.stok?.toLocaleString() || 0} pcs</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedClient.titipan.bahanCair.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-slate-600 mb-3 uppercase tracking-wider border-b border-slate-100 pb-2">Bahan Cair</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {selectedClient.titipan.bahanCair.map(item => (
                              <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-bold text-sm text-[#302621]">{item.nama}</h5>
                                  <span className="bg-sky-100 text-sky-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">{item.kategori}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">Stok Tersedia:</p>
                                <p className="text-lg font-black text-[#8C6208]">{item.stok?.toLocaleString() || 0} {item.satuan}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6 md:w-8 md:h-8 text-[#8C6208]" /> Data Klien
          </h1>
          <p className="text-[10px] md:text-xs text-slate-500 mt-1">
            {filteredEnrichedClients.length} brand klien terdaftar
          </p>
        </div>

        {/* Toolbar: Search & Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 md:min-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari nama brand, owner, pic..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-10 w-full bg-white border-slate-200 focus-visible:ring-[#8C6208] rounded-xl shadow-sm transition-all"
            />
          </div>
          
          <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
            <Select value={sortOption} onValueChange={setSortOption}>
              <SelectTrigger className="w-[180px] h-10 bg-white border-slate-200 rounded-xl shadow-sm text-xs font-semibold text-slate-700">
                <Filter className="w-3.5 h-3.5 text-slate-400 mr-2" />
                <SelectValue placeholder="Urutkan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="terbaru">Terbaru</SelectItem>
                <SelectItem value="terlama">Terlama</SelectItem>
                <SelectItem value="nama_az">Nama (A-Z)</SelectItem>
                <SelectItem value="nama_za">Nama (Z-A)</SelectItem>
                <SelectItem value="po_banyak">Paling Banyak PO</SelectItem>
                <SelectItem value="po_sedikit">Paling Sedikit PO</SelectItem>
              </SelectContent>
            </Select>

            {(sortOption === 'po_banyak' || sortOption === 'po_sedikit') && (
              <Select value={poTimeFilter} onValueChange={setPoTimeFilter}>
                <SelectTrigger className="w-[180px] h-10 bg-[#F9F5EC] border-[#E6D5B8] rounded-xl shadow-sm text-xs font-bold text-[#8C6208]">
                  <Calendar className="w-3.5 h-3.5 mr-2" />
                  <SelectValue placeholder="Waktu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Waktu</SelectItem>
                  <SelectItem value="1_hari">1 Hari Terakhir</SelectItem>
                  <SelectItem value="7_hari">7 Hari Terakhir</SelectItem>
                  <SelectItem value="1_bulan">1 Bulan Terakhir</SelectItem>
                  <SelectItem value="custom">Custom Date</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {poTimeFilter === 'custom' && (sortOption === 'po_banyak' || sortOption === 'po_sedikit') && (
        <div className="flex flex-col sm:flex-row items-center gap-3 p-4 bg-white border border-slate-200 rounded-xl max-w-lg w-full animate-in slide-in-from-top-2 shadow-sm">
          <div className="flex-1 w-full">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Mulai Tanggal</label>
            <CustomDatePicker 
              value={poCustomStart} 
              onChange={setPoCustomStart} 
              placeholder="Pilih tanggal mulai" 
            />
          </div>
          <div className="hidden sm:block text-slate-300 font-light text-2xl pt-4">-</div>
          <div className="flex-1 w-full">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Sampai Tanggal</label>
            <CustomDatePicker 
              value={poCustomEnd} 
              onChange={setPoCustomEnd} 
              placeholder="Pilih tanggal sampai" 
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array(6).fill(0).map((_, i) => <div key={i} className="h-64 bg-muted animate-pulse rounded-3xl" />)}
        </div>
      ) : filteredEnrichedClients.length === 0 ? (
        <div className="text-center py-20 text-slate-400 border border-dashed border-slate-200 rounded-3xl">
          <Building2 className="w-16 h-16 mx-auto mb-3 opacity-20" />
          <p className="text-lg font-medium">Belum ada data klien</p>
          <p className="text-sm mt-1">Klik "Tambah Klien" untuk memulai</p>
        </div>
      ) : (
        <>
        <BatchActionBar
          isSelectionMode={isSelectionMode}
          selectedCount={selectedCount}
          totalCount={filteredEnrichedClients.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onBatchDelete={() => {
            if (window.confirm(`Yakin ingin menghapus ${selectedCount} brand terpilih?`)) {
              batchDeleteMutation.mutate(Array.from(selectedIds));
            }
          }}
          isDeleting={batchDeleteMutation.isPending}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-4">
          {filteredEnrichedClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(client => {
            const statusColor = client.mood === 'happy' ? 'border-emerald-400/50' 
              : client.mood === 'neutral' ? 'border-[#8C6208]/30' 
              : client.mood === 'angry' ? 'border-rose-400/50' 
              : client.mood === 'pasif' ? 'border-slate-300 opacity-75'
              : 'border-slate-200 grayscale opacity-60';
            const gradientColor = client.mood === 'happy' ? 'bg-gradient-to-r from-emerald-500 to-teal-400' 
              : client.mood === 'neutral' ? 'bg-gradient-to-r from-[#8C6208] to-[#B38C3B]' 
              : client.mood === 'angry' ? 'bg-gradient-to-r from-rose-500 to-pink-500'
              : client.mood === 'pasif' ? 'bg-gradient-to-r from-slate-500 to-slate-400'
              : 'bg-gradient-to-r from-slate-400 to-slate-300';
            return (
              <Card 
                key={client.id} 
                className={`group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-2 ${statusColor} rounded-3xl overflow-hidden relative ${selectedIds.has(client.id) ? 'ring-4 ring-[#8C6208]/50 border-transparent' : ''}`}
                onClick={(e) => {
                  if (e.target.closest('.action-btn')) return;
                  if (isSelectionMode) {
                    toggleSelection(client.id);
                  } else {
                    setSelectedClient(client);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  startSelection(client.id);
                }}
              >
                {/* Selection Overlay */}
                {isSelectionMode && (
                  <div 
                    className="absolute top-4 left-4 z-20 cursor-pointer p-1 -m-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(client.id);
                    }}
                  >
                    {selectedIds.has(client.id) ? (
                      <div className="w-6 h-6 rounded-full bg-rose-600 flex items-center justify-center shadow-md">
                         <CheckSquare className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-slate-300 bg-white/80 hover:border-rose-400 shadow-sm transition-all" />
                    )}
                  </div>
                )}
                
                {/* Action Buttons (Top Right) */}
                {!isSelectionMode && (
                  <div className="absolute top-4 right-4 z-20 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      className="action-btn w-8 h-8 rounded-full bg-white/90 shadow-md flex items-center justify-center text-slate-700 hover:text-blue-600 hover:bg-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClient(client);
                        setEditForm(client);
                        setIsEditingClient(true);
                      }}
                      title="Edit Brand"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button 
                      className="action-btn w-8 h-8 rounded-full bg-white/90 shadow-md flex items-center justify-center text-slate-700 hover:text-rose-600 hover:bg-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        startSelection(client.id);
                      }}
                      title="Pilih untuk dihapus"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <CardContent className="p-0">
                   <div className={`h-20 ${gradientColor} relative overflow-hidden`}>
                     <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" />
                   </div>
                   
                   <div className="px-6 pb-6 pt-0 relative">
                     <div className="flex justify-between items-end -mt-8 mb-4">
                        <div className="w-16 h-16 bg-white rounded-2xl shadow-lg border border-slate-100 flex items-center justify-center">
                          <Building2 className={`w-8 h-8 ${client.mood === 'inactive' || client.mood === 'pasif' ? 'text-slate-400' : client.mood === 'happy' ? 'text-emerald-500' : client.mood === 'neutral' ? 'text-[#8C6208]' : 'text-rose-500'}`} />
                        </div>
                        <div className="w-14 h-14 bg-white rounded-full shadow-md border border-slate-100 flex items-center justify-center -mb-2 z-10 overflow-hidden" title="Mood Klien">
                          <CuteEmoji mood={client.mood} className="w-12 h-12" />
                        </div>
                     </div>

                     <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-base md:text-lg font-black text-slate-800">{client.brand_name}</h3>
                          <p className="text-[10px] md:text-[11px] font-semibold text-slate-500 mt-0.5">
                            <span className="text-slate-400">Owner:</span> {client.owner_name || '-'}
                          </p>
                          <p className="text-[10px] md:text-[11px] font-semibold text-slate-500">
                            <span className="text-slate-400">PIC Marketing:</span> {client.pic_name || '-'}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                          client.isUnverified ? 'bg-slate-100 text-slate-500' :
                          client.activePOsCount > 0 ? 'bg-emerald-50 text-emerald-600' : client.mood === 'pasif' ? 'bg-slate-100 text-slate-500' : client.mood === 'inactive' ? 'bg-slate-100 text-slate-400' : 'bg-emerald-50 text-emerald-600'
                        }`}>
                          {client.isUnverified ? 'Belum Terverifikasi' : client.activePOsCount > 0 ? 'Active' : client.mood === 'pasif' ? 'Pasif' : client.mood === 'inactive' ? 'Inactive' : 'Active'}
                        </span>
                     </div>

                     <div className="grid grid-cols-2 gap-4 py-4 border-t border-slate-100 mb-2">
                        <div>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Total PO</p>
                          <p className="text-sm font-black text-slate-700 mt-1">{client.sortPoCount !== undefined ? client.sortPoCount : client.totalOrders} Kali</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Join</p>
                          <p className="text-sm font-black text-slate-700 mt-1">{client.created_date ? new Date(client.created_date).getFullYear() : '-'}</p>
                        </div>
                     </div>

                     <div className="flex items-center justify-between text-sm text-slate-500 font-semibold group-hover:text-[#8C6208] transition-colors">
                        Lihat Detail Histori
                        <div className="w-8 h-8 rounded-full bg-slate-50 group-hover:bg-[#8C6208]/10 flex items-center justify-center transition-colors">
                          <ChevronRight className="w-4 h-4" />
                        </div>
                     </div>
                   </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* ── Pagination Controls ── */}
          {filteredEnrichedClients.length > 0 && (
            <div className="md:col-span-2 xl:col-span-3 px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-2">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filteredEnrichedClients.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(enrichedClients.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(enrichedClients.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

    </div>
  );
}
